// src/components/common/SideNav.js
// Filepath: // src/components/common/SideNav.js
import React from "react";
import {
  Avatar,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import BrushIcon from "@mui/icons-material/Brush"; // paintbrush for "My Studio"
import CollectionsIcon from "@mui/icons-material/Collections";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
// import AccountCircleIcon from "@mui/icons-material/AccountCircle"; // commented out with profile item
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { motion, AnimatePresence } from "framer-motion";
import { useScene } from "../../state/SceneContext";
import logoSrc from "../../assets/logo-filled.png";
import { signOutSafely } from "../../services/authService";
// import { useHomeDataStore } from "../../state/homeDataStore"; // not needed with simplified refresh

const COLLAPSED_W = 72;
const EXPANDED_W = 280;
const ICON_COL_W = COLLAPSED_W;
const CIRCLE = 44;
const ICON_FS = 24;
const LABEL_FS = 20;

// Static nav definitions (stable across renders)
const NAV_TOP = [
  { key: "home", label: "Home", icon: HomeIcon, scene: { name: "home" } },
  {
    key: "purchases",
    label: "Purchases",
    icon: ShoppingBagIcon,
    scene: {
      name: "grid",
      params: { source: "purchases", backTo: "home", sceneKey: "grid-purchases" },
    },
  },

  // "My Activity" -> back to "My Studio" with paintbrush icon
  {
    key: "activity",
    label: "My Studio",
    icon: BrushIcon,
    scene: {
      name: "grid",
      params: { source: "activity", backTo: "home", sceneKey: "grid-activity" },
    },
  },

  {
    key: "collections",
    label: "My Collections",
    icon: CollectionsIcon,
    scene: {
      name: "grid",
      params: { source: "collections", backTo: "home", sceneKey: "grid-collections" },
    },
  },
];

// Bottom items that come BEFORE Sign out/Exit (we’ll append those dynamically)
const NAV_BOTTOM = [{ key: "settings", label: "Settings", icon: SettingsIcon, scene: { name: "settings" } }];

export default function SideNav({
  onSelect,
  selectedKey, // optional override for selected highlight
  user,
  defaultOpen = false,
  focusKey = null, // optional: which item to auto-focus when opened
}) {
  const theme = useTheme();
  const { scene, setScene, replaceScene } = useScene();

  const panelRef = React.useRef(null);
  const [open, setOpen] = React.useState(defaultOpen);
  const [focusedKey, setFocusedKey] = React.useState(null);

  // Basic user-derived fallback for potential future profile use
  const profileDisplayName = React.useMemo(() => user?.displayName || "My Profile", [user]);
  const profilePhoto = React.useMemo(() => user?.photoURL || "", [user]);
  const initials = React.useMemo(() => {
    const dn = profileDisplayName || "Profile";
    const ps = dn.trim().split(/\s+/);
    return ps.length >= 2 ? (ps[0][0] + ps[1][0]).toUpperCase() : (dn[0] || "P").toUpperCase();
  }, [profileDisplayName]);

  // Build full menu once. With Profile hidden, this list is static.
  const { ALL, mainCount } = React.useMemo(() => {
    // Simplified refresh: always enabled, static tooltip
    const refreshItem = {
      key: "refresh",
      label: "Refresh Content",
      kind: "item",
      icon: RefreshIcon,
      action: "refresh",
      disabled: false,
      tooltip: "Reload data from server",
    };

    const settingsItems = NAV_BOTTOM.map((n) => ({ ...n, kind: "item" }));

    // Commented out per request: "My Profile" menu entry (keep for later)
    // const profileItem = {
    //   key: "profile",
    //   label: profileDisplayName,
    //   kind: "profile",
    //   icon: AccountCircleIcon,
    //   scene: { name: "profile", params: { user: "me" } },
    // };

    // Explicit Sign out under bottom section
    const signOutItem = {
      key: "signout",
      label: "Sign out",
      kind: "item",
      icon: LogoutIcon,
      action: "signout",
    };

    // Exit last (OS-level exit / demo behavior)
    const exitItem = {
      key: "exit",
      label: "Exit",
      kind: "item",
      icon: PowerSettingsNewIcon,
      action: "exit",
    };

    const main = NAV_TOP.map((n) => ({ ...n, kind: "item" }));
    // Bottom now excludes profileItem
    const bottom = [refreshItem, ...settingsItems, signOutItem, exitItem];

    return { ALL: [...main, ...bottom], mainCount: main.length };
  }, []); // <-- fixed: no unnecessary dependencies

  // Collapse when remote tells us
  React.useEffect(() => {
    const handler = () => setOpen(false);
    window.addEventListener("sidenav-collapse", handler);
    return () => window.removeEventListener("sidenav-collapse", handler);
  }, []);

  // Keep open in sync if parent requests it
  React.useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  // Open on focus entering the panel (keyboard/mouse)
  React.useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const onFocusIn = () => setOpen(true);
    el.addEventListener("focusin", onFocusIn);
    return () => el.removeEventListener("focusin", onFocusIn);
  }, []);

  // Selected (current scene) detection
  const derivedSelectedKey = React.useMemo(() => {
    if (selectedKey) return selectedKey;
    const s = scene || {};
    const match = ALL.find((it) => {
      if (!it.scene) return false;
      if (it.scene.name !== s.name) return false;
      const navSource = it.scene.params?.source;
      const currSource = s.params?.source;
      if (navSource && navSource !== currSource) return false;
      // Keep this logic; harmless even without a profile item present
      if (it.key === "profile" && s.name === "profile") {
        return s.params?.user ? s.params.user === "me" : true;
      }
      return true;
    });
    return match?.key || "home";
  }, [scene, ALL, selectedKey]);

  const isKeySelected = (key) => derivedSelectedKey === key;

  // Visual circle
  const circle = ({ isFocused, isSelected, isHomeWhenClosed }) => ({
    width: CIRCLE,
    height: CIRCLE,
    borderRadius: "999px",
    display: "grid",
    placeItems: "center",
    transition: "background-color 160ms ease, color 160ms ease, outline-color 160ms ease",
    backgroundColor: (open ? isFocused : isHomeWhenClosed) ? theme.palette.common.white : "transparent",
    color: (open ? isFocused : isHomeWhenClosed) ? "black" : "white",
    outline: isSelected ? "2px solid rgba(255,255,255,0.28)" : "2px solid transparent",
    outlineOffset: 0,
  });

  const navigate = (sceneObj) => {
    if (!sceneObj) return;
    (replaceScene || setScene)(sceneObj);
  };

  // async handler to cover sign-out, refresh, exit, and scene nav
  const handleSelect = async (item) => {
    if (!item || item.disabled) return;

    if (item?.action === "refresh") {
      // Simplified: jump to GreetingScene, which will refetch and then route to Home
      (replaceScene || setScene)({ name: "greeting", params: { reason: "refresh" } });
      onSelect?.(item.key);
      return;
    }

    if (item?.action === "signout") {
      try {
        await signOutSafely();
      } finally {
        // Force a hard reload so IntroPairingScene shows cleanly
        window.location.reload();
      }
      onSelect?.(item.key);
      return;
    }

    if (item?.action === "exit") {
      window.dispatchEvent(new CustomEvent("request-exit-app"));
      onSelect?.(item.key);
      return;
    }

    if (item?.scene) navigate(item.scene);
    onSelect?.(item.key);
  };

  // ---- Focus helpers -------------------------------------------------------
  const focusByKey = React.useCallback(
    (key) => {
      const idx = ALL.findIndex((it) => it.key === key);
      if (idx < 0) return false;

      // Clear ANY previous .focused in the whole document to avoid dual-focus
      document.querySelectorAll(".focused").forEach((n) => n.classList.remove("focused"));

      const el = panelRef.current?.querySelector?.(
        `[data-focusable="true"][data-row="${idx}"][data-col="0"]`
      );
      if (!el) return false;

      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      el.classList.add("focused");
      el.focus({ preventScroll: true });
      setFocusedKey(key);
      return true;
    },
    [ALL]
  );

  // When nav opens: ensure a REAL focus. Default to focusKey or "home".
  React.useEffect(() => {
    if (!open) {
      panelRef.current
        ?.querySelectorAll?.(".focused")
        .forEach((n) => n.classList.remove("focused"));
      setFocusedKey("home"); // visual-only when collapsed
      return;
    }
    const hasFocused = !!panelRef.current?.querySelector?.(".focused");
    if (!hasFocused) {
      const key = focusKey || "home";
      requestAnimationFrame(() => focusByKey(key) || focusByKey("home"));
    }
  }, [open, focusKey, focusByKey]);

  // If parent changes focusKey later (e.g., coming back from GridScene), respect it
  React.useEffect(() => {
    if (!focusKey || !open) return;
    const alreadyFocused = !!panelRef.current?.querySelector?.(".focused");
    if (alreadyFocused) return;
    requestAnimationFrame(() => focusByKey(focusKey) || focusByKey("home"));
  }, [focusKey, open, focusByKey]);

  const Row = ({ n, rowIndex }) => {
    const isProfile = n.kind === "profile";
    const isFocused = focusedKey === n.key;
    const isSelected = isKeySelected(n.key);
    const IconCmp = n.icon;
    const btnId = `nav-btn-${n.key}`;

    // Tooltip: for the refresh item, prefer static tooltip when closed
    const tooltipWhenClosed = n.tooltip || n.label;

    return (
      <Box
        data-focusable="true"
        data-row={rowIndex}
        data-col={0}
        className={isFocused && open ? "focused" : ""}
        tabIndex={-1}
        role="button"
        aria-label={n.label}
        data-click-target={`#${btnId}`}
        onFocus={() => setFocusedKey(n.key)}
        onClick={() => handleSelect(n)}
        sx={{
          mb: 0.5,
          borderRadius: "10px",
          "&, & *:focus, & *:focus-visible": {
            outline: "none !important",
            boxShadow: "none !important",
            border: "none !important",
          },
          opacity: n.disabled ? 0.6 : 1,
          pointerEvents: n.disabled ? "none" : "auto",
        }}
      >
        <Tooltip title={!open ? tooltipWhenClosed : ""} placement="right">
          <ListItemButton
            id={btnId}
            disableRipple
            selected={isSelected}
            disabled={Boolean(n.disabled)}
            sx={{
              px: 0,
              py: 1,
              borderRadius: "10px",
              "&.Mui-selected": { background: "transparent" },
              "&:hover": { background: "rgba(255,255,255,0.08)" },
              display: "grid",
              gridTemplateColumns: `${ICON_COL_W}px ${open ? "1fr" : "0px"}`,
              alignItems: "center",
            }}
          >
            <Box sx={{ width: ICON_COL_W, display: "grid", placeItems: "center" }}>
              <Box
                sx={circle({
                  isFocused,
                  isSelected,
                  isHomeWhenClosed: !open && n.key === "home",
                })}
              >
                {isProfile ? (
                  <Avatar
                    src={profilePhoto}
                    alt={profileDisplayName}
                    sx={{
                      width: CIRCLE - 10,
                      height: CIRCLE - 10,
                      fontSize: 13,
                      bgcolor: "transparent",
                      color: (open ? isFocused : n.key === "home") ? "black" : "white",
                    }}
                  >
                    {!profilePhoto ? initials : null}
                  </Avatar>
                ) : IconCmp ? (
                  <IconCmp sx={{ fontSize: ICON_FS, color: "inherit" }} />
                ) : null}
              </Box>
            </Box>

            <motion.div
              initial={false}
              animate={{ opacity: open ? 1 : 0, width: open ? "auto" : 0 }}
              style={{ overflow: "hidden", whiteSpace: "nowrap" }}
            >
              <ListItemText
                primary={n.label}
                primaryTypographyProps={{
                  fontWeight: 800,
                  fontSize: LABEL_FS,
                  letterSpacing: 0.2,
                }}
              />
            </motion.div>
          </ListItemButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="sidenav-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 1300,
            }}
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>

      <motion.div
        key="sidenav-panel"
        ref={panelRef}
        initial={false}
        animate={{ width: open ? EXPANDED_W : COLLAPSED_W }}
        transition={{ type: "spring", stiffness: 420, damping: 36 }}
        style={{
          position: "fixed",
          top: 24,
          bottom: 24,
          left: 24,
          zIndex: 1350,
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          pointerEvents: "auto",
          boxShadow: "0 10px 40px rgba(0,0,0,0.45)",
          backdropFilter: "saturate(120%)",
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        data-scope="sidenav"
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "rgba(0,0,0,0.9)",
            color: theme.palette.common.white,
            "& .focused, & .focused *:focus, & .focused *:focus-visible, & [data-focusable='true']:focus, & [data-focusable='true']:focus-visible": {
              outline: "none !important",
              boxShadow: "none !important",
              border: "none !important",
            },
          }}
        >
          <Box sx={{ position: "relative", height: 84 }}>
            <motion.img
              src={logoSrc}
              alt="Kbean"
              initial={false}
              animate={{ y: open ? 0 : -14, scale: open ? 1 : 0.33, opacity: 1 }}
              transition={{ type: "spring", stiffness: 360, damping: 28 }}
              style={{
                position: "absolute",
                top: open ? "50px" : "30px",
                left: "50%",
                transformOrigin: "center top",
                translate: "-50% 0",
                width: open ? 120 : 150,
                height: "auto",
                display: "block",
              }}
            />
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <List dense disablePadding>
              {ALL.slice(0, mainCount).map((n, i) => (
                <Row key={n.key} n={n} rowIndex={i} />
              ))}
            </List>
          </Box>

          <Divider sx={{ opacity: 0.18, borderColor: "rgba(255,255,255,0.25)" }} />

          <Box sx={{ pt: 0.5, pb: 1 }}>
            <List dense disablePadding>
              {ALL.slice(mainCount).map((n, i) => (
                <Row key={n.key} n={n} rowIndex={mainCount + i} />
              ))}
            </List>
          </Box>
        </Box>
      </motion.div>
    </>
  );
}
